import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ShoppingOutlined, MinusOutlined, PlusOutlined, CloseOutlined } from '@ant-design/icons';
import { AppState } from '../../../store';
import { removeItem, updateCantidad, clearCarrito } from '../../../store/Carrito/actions';
import type { EntregaRequest } from '../../../store/MSCommerce/Pedidos/types';
import { openNotification } from '../../../globals/Components/Notification/Notification';
import { isAuthenticated } from '../../../Services/auth';
import { EntregaModal } from './EntregaModal';
import { ConfirmacionPedidoModal } from './ConfirmacionPedidoModal';
import styles from './CarritoPage.module.scss';

const CarritoPage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const history = useHistory();
  const items = useSelector((state: AppState) => state.carritoAction.items);
  const total = items.reduce((sum, item) => sum + item.precio * item.cantidad, 0);

  const [showEntrega, setShowEntrega] = useState(false);
  const [entregaData, setEntregaData] = useState<EntregaRequest | null>(null);

  const handleReservar = () => {
    if (!isAuthenticated()) {
      openNotification('', 'Inicia sesión para reservar tus productos');
      history.push('/login');
      return;
    }
    setShowEntrega(true);
  };

  const handleEntregaSubmit = (entrega: EntregaRequest) => {
    setShowEntrega(false);
    setEntregaData(entrega);
  };

  return (
    <div className={styles.carritoPage}>
      <h1 className={styles.carritoPage__title}>Mi carrito</h1>

      {items.length === 0 ? (
        <div className={styles.carritoPage__empty}>
          <ShoppingOutlined className={styles.carritoPage__emptyIcon} />
          <p>Tu carrito está vacío</p>
          <button className={styles.carritoPage__browseBtn} onClick={() => history.push('/products')}>
            Ver productos
          </button>
        </div>
      ) : (
        <div className={styles.carritoPage__content}>
          <div className={styles.carritoPage__list}>
            {items.map(item => (
              <div key={item.productoId} className={styles.carritoPage__item}>
                <div className={styles.carritoPage__itemImage}>
                  {item.imagen ? (
                    <img src={item.imagen} alt={item.nombre} loading="lazy" />
                  ) : (
                    <div className={styles.carritoPage__itemNoImage} />
                  )}
                </div>

                <div className={styles.carritoPage__itemInfo}>
                  <p className={styles.carritoPage__itemName}>{item.nombre}</p>
                  <p className={styles.carritoPage__itemPrice}>${item.precio.toFixed(2)} c/u</p>
                </div>

                <div className={styles.carritoPage__stepper}>
                  <button
                    onClick={() => dispatch(updateCantidad(item.productoId, item.cantidad - 1))}
                    disabled={item.cantidad <= 1}
                  >
                    <MinusOutlined />
                  </button>
                  <span>{item.cantidad}</span>
                  <button
                    onClick={() => dispatch(updateCantidad(item.productoId, item.cantidad + 1))}
                    disabled={item.cantidad >= item.stockDisponible}
                  >
                    <PlusOutlined />
                  </button>
                </div>

                <p className={styles.carritoPage__itemSubtotal}>
                  ${(item.precio * item.cantidad).toFixed(2)}
                </p>

                <button
                  className={styles.carritoPage__removeBtn}
                  onClick={() => dispatch(removeItem(item.productoId))}
                >
                  <CloseOutlined />
                </button>
              </div>
            ))}
          </div>

          <div className={styles.carritoPage__summary}>
            <div className={styles.carritoPage__summaryRow}>
              <span>Total</span>
              <span>${total.toFixed(2)}</span>
            </div>
            <button className={styles.carritoPage__reservarBtn} onClick={handleReservar}>
              Reservar productos
            </button>
          </div>
        </div>
      )}

      {showEntrega && (
        <EntregaModal
          onClose={() => setShowEntrega(false)}
          onSubmit={handleEntregaSubmit}
        />
      )}

      {entregaData && (
        <ConfirmacionPedidoModal
          items={items}
          entrega={entregaData}
          total={total}
          onReservado={() => dispatch(clearCarrito())}
          onClose={() => { setEntregaData(null); history.push('/products'); }}
        />
      )}
    </div>
  );
};

export default CarritoPage;
